import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { 
  BookOpen, 
  LayoutGrid, 
  Users, 
  KanbanSquare, 
  BarChart3, 
  Bell, 
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Zap
} from 'lucide-react';

export default function UserGuide() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-8 w-8 text-indigo-600" />
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Guida all'Uso</h1>
        </div>
        <p className="text-slate-600">Scopri come utilizzare al meglio Lead Manager</p>
      </div>

      {/* Quick Start */}
      <Card className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 mb-8" data-testid="quick-start-card">
        <h2 className="text-xl font-bold mb-4">Inizia Subito</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <span className="text-lg font-bold">1</span>
            </div>
            <div>
              <p className="font-semibold">Dashboard</p>
              <p className="text-indigo-100 text-sm">Controlla le tue statistiche</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <span className="text-lg font-bold">2</span>
            </div>
            <div>
              <p className="font-semibold">Lead</p>
              <p className="text-indigo-100 text-sm">Gestisci i tuoi contatti</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <span className="text-lg font-bold">3</span>
            </div>
            <div>
              <p className="font-semibold">Pipeline</p>
              <p className="text-indigo-100 text-sm">Segui il percorso di vendita</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Sections */}
      <div className="space-y-6">
        
        {/* Dashboard */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-dashboard">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-indigo-50 p-3 rounded-lg">
              <LayoutGrid className="h-6 w-6 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Dashboard</h2>
          </div>
          <p className="text-slate-600 mb-4">
            La Dashboard è la tua pagina principale. Qui puoi vedere a colpo d'occhio:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Lead Totali:</strong> Il numero totale di lead che ti sono stati assegnati</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Nuovi Lead:</strong> I lead appena arrivati che devi ancora contattare</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>In Lavorazione:</strong> I lead che stai attualmente gestendo</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Convertiti:</strong> I lead che hai convertito in clienti</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Tasso di Conversione:</strong> La percentuale di lead convertiti</span>
            </li>
          </ul>
        </Card>

        {/* Lead List */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-leads">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">I Miei Lead</h2>
          </div>
          <p className="text-slate-600 mb-4">
            Qui trovi la lista di tutti i lead assegnati a te. Per ogni lead puoi:
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Cercare:</strong> Usa la barra di ricerca per trovare un lead specifico</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Filtrare:</strong> Seleziona uno stato per vedere solo i lead in quella fase</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Visualizzare dettagli:</strong> Clicca su un lead per vedere tutte le informazioni</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Aggiornare lo stato:</strong> Cambia lo stato del lead man mano che procedi</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Aggiungere note:</strong> Scrivi appunti importanti sul lead</span>
            </li>
          </ul>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Stati disponibili:</p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded text-xs">Nuovo</span>
              <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded text-xs">Telefonata Target</span>
              <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 rounded text-xs">Videochiamata</span>
              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded text-xs">Convertito</span>
              <span className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-1 rounded text-xs">Perso</span>
            </div>
          </div>
        </Card>

        {/* Pipeline */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-pipeline">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-50 p-3 rounded-lg">
              <KanbanSquare className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Pipeline</h2>
          </div>
          <p className="text-slate-600 mb-4">
            La Pipeline ti mostra i tuoi lead organizzati per stato, in stile Kanban:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700">Ogni colonna rappresenta uno stato diverso</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700">Puoi vedere quanti lead hai in ogni fase</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700">Clicca su un lead per modificarlo</span>
            </li>
          </ul>
        </Card>

        {/* Reports */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-reports">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-50 p-3 rounded-lg">
              <BarChart3 className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Report</h2>
          </div>
          <p className="text-slate-600 mb-4">
            Nella sezione Report trovi statistiche dettagliate sulle tue performance:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Periodo:</strong> Seleziona "Settimana" o "Mese" per vedere i dati del periodo</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Grafici:</strong> Visualizza l'andamento dei lead nel tempo</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
              <span className="text-slate-700"><strong>Tasso di conversione:</strong> Monitora la tua efficacia</span>
            </li>
          </ul>
        </Card>

        {/* Notifications */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-notifications">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-50 p-3 rounded-lg">
              <Bell className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Notifiche</h2>
          </div>
          <p className="text-slate-600 mb-4">
            Le notifiche ti avvisano quando succede qualcosa di importante:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Nuovo lead assegnato:</strong> Ricevi una notifica quando ti viene assegnato un nuovo lead</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700"><strong>Lead convertito:</strong> Festeggia quando converti un lead!</span>
            </li>
          </ul>
          <p className="text-slate-500 text-sm mt-4">
            Clicca sulla campanella in alto a sinistra per vedere tutte le tue notifiche.
          </p>
        </Card>

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <Card className="bg-white border-2 border-indigo-200 p-6" data-testid="guide-admin">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <ShieldCheck className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Vista Amministratore</h2>
                <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Solo Admin</span>
              </div>
            </div>
            <p className="text-slate-600 mb-4">
              Come amministratore, hai accesso a funzionalità aggiuntive:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
                <span className="text-slate-700"><strong>Tutti i Lead:</strong> Vedi la lista completa di tutti i lead di tutti i commerciali</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
                <span className="text-slate-700"><strong>Filtro per commerciale:</strong> Filtra i lead per vedere quelli assegnati a un commerciale specifico</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-indigo-500 mt-0.5" />
                <span className="text-slate-700"><strong>Statistiche per commerciale:</strong> Vedi le performance di ogni commerciale</span>
              </li>
            </ul>
          </Card>
        )}

        {/* Zapier Integration */}
        <Card className="bg-white border border-slate-200 p-6" data-testid="guide-zapier">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-orange-50 p-3 rounded-lg">
              <Zap className="h-6 w-6 text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Integrazione Zapier</h2>
          </div>
          <p className="text-slate-600 mb-4">
            I lead possono arrivare automaticamente da Google Sheets tramite Zapier:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">Quando viene aggiunta una nuova riga al foglio Google, il lead viene creato automaticamente</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">Il lead viene assegnato automaticamente a un commerciale (in modo equo/round-robin)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">Il commerciale riceve una notifica per il nuovo lead</span>
            </li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-amber-800">
              <strong>Nota:</strong> Lo Zap deve essere "pubblicato" (attivo) per funzionare. Si attiva solo quando vengono aggiunte NUOVE righe al foglio Google.
            </p>
          </div>
        </Card>

      </div>
    </div>
  );
}
